import { useRef, useState } from 'react';
import { projectLiveFilesAfterReply } from '@l2u/shared/replyAttachment';
import { fromDateInput } from '../lib/expiry.ts';
import { DOCUMENT_ACCEPT } from '../lib/fileTypes.ts';
import { formatFileSize } from '../lib/fileHistory.ts';
import { buildDocumentGroups, type DocVersionFile } from '../lib/documentVersions.ts';
import { attachmentNeedsExpiry } from '../lib/replyComposerGate.ts';
import { replyRemoveAvailable } from '../lib/replyRemoval.ts';
import { Btn } from './Btn.tsx';
import { DateField } from './DateField.tsx';
import { ErrorMessage } from './ErrorMessage.tsx';

export type ComposerMode = 'reference' | 'replace' | 'new';
export type ComposerAttachment = { file: File; expiresAt: string | null; replacesFileIds: string[]; mode: ComposerMode };
/**
 * `closesConversation` is present (and `true`) only when the admin ticked "No reply needed".
 * `removesFileIds` is the current documents the vendor toggled Remove on (`[]` when none;
 * always `[]` from an admin).
 */
export type ReplyPayload = {
  body: string;
  attachments: ComposerAttachment[];
  removesFileIds: string[];
  closesConversation?: boolean;
};

export type ComposerDraft = { key: string; file: File; expiry: string; mode: ComposerMode; targets: string[] };

/**
 * The modes an attached file may take and the one a newly attached file starts in.
 * `keptDocCount` is the current documents this reply does NOT remove. A single-file
 * question with a kept document must replace it — offering "New" would let a second
 * file coexist with the one the question caps at one of. Removing that document brings
 * "New" back (and makes it the default): the new file takes its place.
 */
export function composerModes(p: {
  role: 'vendor' | 'admin';
  canPromote: boolean;
  allowMultipleFiles: boolean;
  keptDocCount: number;
}): { modes: ComposerMode[]; defaultMode: ComposerMode; hideNewMode: boolean } {
  const canReplace = p.canPromote && p.keptDocCount > 0;
  const hideNewMode = !p.allowMultipleFiles && p.keptDocCount > 0;
  const defaultMode: ComposerMode =
    p.role === 'admin' ? 'reference' : canReplace ? 'replace' : 'new';
  const modes: ComposerMode[] =
    p.role === 'admin'
      ? hideNewMode
        ? ['reference', 'replace']
        : ['reference', 'replace', 'new']
      : hideNewMode
        ? ['replace']
        : ['replace', 'new'];
  return { modes, defaultMode, hideNewMode };
}

export function ReplyComposer({
  role,
  canPromote,
  offersExpiry,
  allowMultipleFiles,
  currentFiles,
  onReply,
  pending = false,
  error = null,
  requiresExpiry = false,
  offersClose = false,
  requiresFile = false,
  initialDrafts,
  removals = [],
  onClearRemovals,
}: {
  role: 'vendor' | 'admin';
  /** replyAttachmentCanPromote: an attached file can become the answer (Replaces / New). */
  canPromote: boolean;
  /** replyAttachmentOffersExpiry: the per-file expiry date field renders. */
  offersExpiry: boolean;
  allowMultipleFiles: boolean;
  currentFiles: DocVersionFile[];
  onReply: (payload: ReplyPayload) => void;
  pending?: boolean;
  error?: Error | null;
  requiresExpiry?: boolean;
  /**
   * Admin only: offer "No reply needed — close the conversation" (an approved
   * compliance document). Ignored for a vendor, whatever the caller passes.
   */
  offersClose?: boolean;
  /**
   * The question owes a file if it is left with zero files (the caller derives it from
   * owesFileAfter). Hides Remove on a single-file question, and blocks a reply whose
   * removals would leave no document at all.
   */
  requiresFile?: boolean;
  /** Test seam: seeds the draft list for renderToStaticMarkup. Product code never passes it. */
  initialDrafts?: ComposerDraft[];
  /**
   * Vendor only: the current documents toggled Remove on their file rows (the caller
   * renders the toggles — DocumentTable / the file list — and owns this state). They are
   * sent with the reply as `removesFileIds`.
   */
  removals?: string[];
  /** Called once the reply is handed to `onReply`, so the caller resets its toggles. */
  onClearRemovals?: () => void;
}) {
  const [body, setBody] = useState('');
  const [drafts, setDrafts] = useState<ComposerDraft[]>(initialDrafts ?? []);
  const [closes, setCloses] = useState(false);
  const showClose = role === 'admin' && offersClose;
  const seq = useRef(0);
  const fileInput = useRef<HTMLInputElement>(null);

  // Current documents that can be replacement targets. A group whose head is
  // superseded is history-only (every file in it has been replaced already) —
  // it must not be offered as something a new file can replace.
  const targetDocs = buildDocumentGroups(currentFiles)
    .filter((g) => g.current.superseded_at === null)
    .map((g) => ({ id: g.current.id, label: g.current.file_name }));
  // The same rule the caller uses to show the row toggles (replyRemoveAvailable).
  const removeAvailable = replyRemoveAvailable({
    role,
    canPromote,
    allowMultipleFiles,
    requiresFile,
    hasCurrentDocs: targetDocs.length > 0,
  });
  // Only removals the vendor can still see count: an id a refetch has since superseded
  // (or a toggle that has since hidden) would otherwise be sent stale or block Send
  // with no Undo on screen.
  const targetIds = new Set(targetDocs.map((d) => d.id));
  const activeRemovals = removeAvailable ? removals.filter((id) => targetIds.has(id)) : [];
  const removedNames = targetDocs.filter((d) => activeRemovals.includes(d.id)).map((d) => d.label);
  // Documents still current after this reply's removals drive the mode rules.
  const keptDocCount = targetDocs.filter((d) => !activeRemovals.includes(d.id)).length;
  const { modes, defaultMode, hideNewMode } = composerModes({
    role,
    canPromote,
    allowMultipleFiles,
    keptDocCount,
  });

  function addFiles(list: FileList | null) {
    if (!list) return;
    const added: ComposerDraft[] = Array.from(list).map((file) => {
      seq.current += 1;
      return { key: `f${seq.current}`, file, expiry: '', mode: defaultMode, targets: [] };
    });
    setDrafts((d) => [...d, ...added]);
    if (fileInput.current) fileInput.current.value = '';
  }
  const patch = (key: string, p: Partial<ComposerDraft>) => setDrafts((d) => d.map((x) => (x.key === key ? { ...x, ...p } : x)));
  const remove = (key: string) => setDrafts((d) => d.filter((x) => x.key !== key));
  const toggleTarget = (key: string, id: string) =>
    setDrafts((d) => d.map((x) => (x.key !== key ? x : { ...x, targets: x.targets.includes(id) ? x.targets.filter((t) => t !== id) : [...x.targets, id] })));

  // The removal toggles live on the caller's file rows, so the drafts are never told
  // when one flips. Derive instead of mutating:
  // - a document cannot be both removed and replaced (the server refuses it), so a
  //   removed one drops out of every draft's Replaces targets (Undo restores the pick);
  // - Undo on a single-file question brings its document back and "New" goes away with
  //   it, so a draft left on New reads as Replaces (target still to pick).
  const effective = drafts.map((d) => ({
    ...d,
    mode: hideNewMode && d.mode === 'new' ? ('replace' as const) : d.mode,
    targets: d.targets.filter((t) => !activeRemovals.includes(t)),
  }));

  // Duplicate-target guard (mirrors the server rule).
  const seen = new Set<string>();
  let dup = false;
  for (const dft of effective) if (dft.mode === 'replace') for (const t of dft.targets) { if (seen.has(t)) dup = true; seen.add(t); }

  const answerCount = effective.filter((d) => canPromote && (role === 'vendor' || d.mode !== 'reference')).length;
  const replCount = seen.size;

  // A replacement/answer document on a requires_expiry question must carry a fresh
  // expiry date — mirror the server rule and prompt for it before sending.
  const draftNeedsExpiry = (d: ComposerDraft) =>
    attachmentNeedsExpiry(role, d.mode, canPromote, requiresExpiry, offersExpiry);
  const expiryMissingCount = effective.filter((d) => draftNeedsExpiry(d) && !d.expiry).length;

  // Mirror of the server's requirement refusal: removals that would leave a file-owing
  // question with no document at all (after replacements and new answer files) block Send.
  const blockedByRequirement =
    requiresFile &&
    activeRemovals.length > 0 &&
    projectLiveFilesAfterReply({
      liveIds: targetDocs.map((d) => d.id),
      removesFileIds: activeRemovals,
      replacesFileIds: [...seen],
      newAnswerFileCount: answerCount,
      allowMultipleFiles,
    }).length === 0;

  const canSubmit =
    !pending && !dup && !blockedByRequirement &&
    (body.trim().length > 0 || drafts.length > 0 || activeRemovals.length > 0) &&
    // A Replaces draft needs a target. This also covers "New" beside a kept document on
    // a single-file question (refused server-side): `effective` reads it as Replaces.
    effective.every((d) => !(d.mode === 'replace' && d.targets.length === 0)) &&
    expiryMissingCount === 0;

  function submit() {
    if (!canSubmit) return;
    onReply({
      body: body.trim(),
      attachments: effective.map((d) => ({
        file: d.file,
        expiresAt: d.expiry ? fromDateInput(d.expiry) : null,
        replacesFileIds: d.mode === 'replace' ? d.targets : [],
        mode: d.mode,
      })),
      removesFileIds: activeRemovals,
      // exactOptionalPropertyTypes: omit the key when unticked, never `false`/`undefined`.
      ...(showClose && closes ? { closesConversation: true } : {}),
    });
    setBody('');
    setDrafts([]);
    onClearRemovals?.();
    setCloses(false);
  }

  const modeLabel: Record<ComposerMode, string> = { reference: 'Reference', replace: 'Replaces', new: 'New' };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <textarea
        className="min-h-[60px] w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4D148C]"
        placeholder="Write a reply…"
        value={body}
        disabled={pending}
        onChange={(e) => setBody(e.target.value)}
      />

      {(drafts.length > 0 || removedNames.length > 0) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
          {drafts.length > 0 && (
            <span className="rounded-full border border-[#4D148C]/20 bg-[#F3EBFA] px-2.5 py-0.5 font-semibold text-[#4D148C]">
              {drafts.length} file{drafts.length === 1 ? '' : 's'} attached
            </span>
          )}
          {drafts.length > 0 && canPromote && <span>Replacing {replCount} · {answerCount - replCount < 0 ? 0 : answerCount - replCount} new</span>}
          {/* The toggles sit on the file rows above, so say here what Send will remove. */}
          {removedNames.length > 0 && (
            <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 font-semibold text-red-700">
              Removing {removedNames.join(', ')}
            </span>
          )}
        </div>
      )}

      {dup && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Two files are set to replace the same document. Each document can only be replaced once.
        </div>
      )}

      {expiryMissingCount > 0 && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {expiryMissingCount} file{expiryMissingCount === 1 ? '' : 's'} still need
          {expiryMissingCount === 1 ? 's' : ''} an expiry date before you can send.
        </div>
      )}

      {blockedByRequirement && (
        <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          This question needs at least one document. Attach a replacement before removing the last one.
        </div>
      )}

      <div className="mt-2.5 flex flex-col gap-3">
        {effective.map((d) => (
          <div key={d.key} className="overflow-hidden rounded-[10px] border border-slate-300">
            <div className="flex items-center gap-3 border-b border-[#4D148C]/20 bg-[#F3EBFA] px-3.5 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{d.file.name}</div>
                <div className="text-[11px] text-slate-500">{formatFileSize(d.file.size)}</div>
              </div>
              <button type="button" onClick={() => remove(d.key)} disabled={pending}
                      className="rounded p-1 text-slate-500 hover:bg-white" aria-label="Remove file">✕</button>
            </div>
            {canPromote && (
              <div className="flex flex-col gap-3 p-3.5">
                <div className="flex flex-wrap items-end gap-3.5">
                  {offersExpiry && (
                    <div className="min-w-[150px] flex-1">
                      <label className="mb-1.5 block text-xs font-semibold text-slate-700">
                        Expiry date
                        {draftNeedsExpiry(d) && <span className="ml-0.5 text-red-600">*</span>}
                      </label>
                      <DateField
                        value={d.expiry}
                        onChange={(v) => patch(d.key, { expiry: v })}
                        disabled={pending}
                        ariaLabel="Document expiry date"
                        placeholder={draftNeedsExpiry(d) ? 'Expiry date required' : 'Select date'}
                        invalid={draftNeedsExpiry(d) && !d.expiry}
                      />
                      {draftNeedsExpiry(d) && !d.expiry && (
                        <span className="mt-1 block text-xs font-medium text-red-600">
                          This replacement needs a new expiry date.
                        </span>
                      )}
                    </div>
                  )}
                  <div className="min-w-[150px] flex-1">
                    <label className="mb-1.5 block text-xs font-semibold text-slate-700">This file</label>
                    <div className="flex gap-1 rounded-lg bg-slate-100 p-1">
                      {modes.map((m) => (
                        <button key={m} type="button" disabled={pending}
                                onClick={() => patch(d.key, { mode: m, targets: m === 'replace' ? d.targets : [] })}
                                className={`flex-1 rounded-md px-2.5 py-2 text-[13px] font-semibold ${d.mode === m ? 'bg-white text-[#4D148C] shadow-sm' : 'text-slate-600'}`}>
                          {modeLabel[m]}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {d.mode === 'replace' && (
                  <div>
                    <div className="mb-1.5 text-xs font-semibold text-slate-700">Which documents does this file replace?</div>
                    <div className="flex flex-col gap-2">
                      {targetDocs.map((doc) => {
                        const checked = d.targets.includes(doc.id);
                        return (
                          <label key={doc.id} className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2.5 ${checked ? 'border-[#4D148C]/30 bg-[#F3EBFA]/60' : 'border-slate-200'}`}>
                            <input type="checkbox" checked={checked} disabled={pending || activeRemovals.includes(doc.id)}
                                   onChange={() => toggleTarget(d.key, doc.id)} className="h-4 w-4 accent-[#4D148C]" />
                            <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-700">{doc.label}</span>
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Current</span>
                          </label>
                        );
                      })}
                    </div>
                    {d.targets.length === 0 && (
                      <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                        Select at least one document this file replaces, or switch it to "New".
                      </div>
                    )}
                  </div>
                )}
                {d.mode === 'new' && (
                  <div className="rounded-lg border border-[#4D148C]/20 bg-[#F3EBFA] px-3 py-2 text-xs text-[#4D148C]">
                    Added as an additional document. Nothing is replaced.
                  </div>
                )}
                {d.mode === 'reference' && (
                  <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                    Sent as a reference example only — it does not become the vendor's document.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      <input ref={fileInput} type="file" accept={DOCUMENT_ACCEPT} multiple
             className="hidden" disabled={pending} onChange={(e) => addFiles(e.target.files)} />
      <button type="button" onClick={() => fileInput.current?.click()} disabled={pending}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white py-2.5 text-[13px] font-semibold text-slate-600 hover:bg-slate-50">
        + Attach {drafts.length > 0 ? 'another file' : 'a document'}
      </button>

      {showClose && (
        <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={closes}
            disabled={pending}
            onChange={(e) => setCloses(e.target.checked)}
            aria-label="No reply needed — close the conversation"
            className="h-4 w-4 accent-[#4D148C]"
          />
          No reply needed — close the conversation
        </label>
      )}

      <div className="mt-3.5 flex items-center justify-between gap-3 border-t border-slate-200 pt-3.5">
        <div className="max-w-[330px] text-[11px] text-slate-500">Replaced and removed documents move to version history when you send.</div>
        <Btn size="sm" variant="primary" disabled={!canSubmit} loading={pending} onClick={submit}>Send reply</Btn>
      </div>
      {error && <div className="mt-2"><ErrorMessage error={error} /></div>}
    </div>
  );
}
